import { useQuery } from "@tanstack/react-query";
import apiClient from "./apiClient";
import { ENDPOINTS } from "./endpoints";
import queryKeys from "../queryKeys";
import { isSessionValid, getSessionToken } from "../../features/subscription/utils/session";

export const subscribe = async (body) => {
    return apiClient.post(ENDPOINTS.subscriptions, body);
};

export const subscribeToIncident = async (incidentId, body) => {
    return apiClient.post(ENDPOINTS.subscribeIncident(incidentId), body);
};

export const subscribeToMaintenance = async (maintenanceId, body) => {
    return apiClient.post(ENDPOINTS.subscribeMaintenance(maintenanceId), body);
};

export const subscribeToAdvisory = async (advisoryId, body) => {
    return apiClient.post(ENDPOINTS.subscribeAdvisory(advisoryId), body);
};

export const unsubscribe = async (body) => {
    return apiClient.post(ENDPOINTS.unsubscribe, body);
};

export const getSubscriptions = async (query) => {
    return apiClient.get(`${ENDPOINTS.subscriptions}${query}`);
};

/**
 * Powers the per-event Watch/Watching toggle (EventWatchButton). Reads the same
 * GET /subscriptions/{email} response as the manage-subscriptions review flow, but as a
 * React Query cache so multiple buttons on a page (IncidentsPage, AdvisoriesPage, etc.)
 * share one fetch and one invalidation point.
 *
 * `retry: false` because a 404 (no subscriptions yet for this email) is a normal empty
 * state, not a transient failure -- see the same 404 handling in useSubscriptionFlow.
 */
export const useFetchSubscriptions = (email) => {
    return useQuery({
        queryKey: queryKeys.subscriptions(email),
        queryFn: async () => {
            try {
                const result = await getSubscriptions(`/${email}?session_token=${getSessionToken()}`);
                return result?.data || {};
            } catch (error) {
                if (error?.response?.status === 404) return {};
                throw error;
            }
        },
        enabled: !!email && isSessionValid(),
        retry: false,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * Generic invalidation helper shared by useWatchEvent/useUnwatchEvent in
 * features/subscription/hooks/useEventSubscriptions.js, kept here so both mutations
 * invalidate the exact same query key this file's own useFetchSubscriptions reads.
 */
export const invalidateSubscriptions = (queryClient, email) =>
    queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions(email) });


